import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { pb } from '../../lib/pocketbase'
import type { BioItem } from './types'
import { BIO_CATEGORIES } from './categories'
import { bioItemImageUrl, normalizeBioLink } from './utils'

const DESKTOP_POSTS_PER_PAGE = 50
const MOBILE_POSTS_PER_PAGE = 30

export function LinkBioPage() {
  const [banners, setBanners] = useState<BioItem[]>([])
  const [posts, setPosts] = useState<BioItem[]>([])
  const [featuredPosts, setFeaturedPosts] = useState<BioItem[]>([])
  const [totalPosts, setTotalPosts] = useState(0)
  const [postPage, setPostPage] = useState(1)
  const [activeBanner, setActiveBanner] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [activeCategory, setActiveCategory] = useState<string>('TODAS')
  const [postsPerPage, setPostsPerPage] = useState(DESKTOP_POSTS_PER_PAGE)

  const loadBanners = useCallback(() => {
    return pb.collection('link_bio_items').getFullList<BioItem>({ filter: 'kind = "banner"', sort: 'position' }).then(setBanners)
  }, [])

  const reloadCurrentPosts = useCallback(async () => {
    const [featured, response] = await Promise.all([
      loadFeaturedPosts(activeCategory),
      loadPostsPage(activeCategory, 1, postsPerPage),
    ])
    setFeaturedPosts(featured)
    setPosts(response.items)
    setTotalPosts(response.totalItems)
    setPostPage(1)
  }, [activeCategory, postsPerPage])

  useEffect(() => {
    void loadBanners()
  }, [loadBanners])

  useEffect(() => {
    if (!banners.length) return
    setActiveBanner((current) => Math.min(current, Math.max(banners.length - 1, 0)))
  }, [banners.length])

  useEffect(() => {
    const query = window.matchMedia('(max-width: 768px)')
    const syncPageSize = () => setPostsPerPage(query.matches ? MOBILE_POSTS_PER_PAGE : DESKTOP_POSTS_PER_PAGE)
    syncPageSize()
    query.addEventListener('change', syncPageSize)
    return () => query.removeEventListener('change', syncPageSize)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setPosts([])
    setFeaturedPosts([])
    setPostPage(1)

    Promise.all([
      loadFeaturedPosts(activeCategory),
      loadPostsPage(activeCategory, 1, postsPerPage),
    ]).then(([featured, response]) => {
      if (cancelled) return
      setFeaturedPosts(featured)
      setPosts(response.items)
      setTotalPosts(response.totalItems)
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [activeCategory, postsPerPage])

  useEffect(() => {
    let cancelled = false
    let poll: number | undefined

    const refresh = (kind?: BioItem['kind']) => {
      if (cancelled) return
      if (!kind || kind === 'banner') void loadBanners()
      if (!kind || kind === 'post') void reloadCurrentPosts()
    }

    pb.collection('link_bio_items').subscribe('*', (event) => {
      refresh(((event.record as unknown) as BioItem | undefined)?.kind)
    }).catch(() => {
      if (!cancelled) poll = window.setInterval(() => refresh(), 30000)
    })

    return () => {
      cancelled = true
      if (poll) window.clearInterval(poll)
      void pb.collection('link_bio_items').unsubscribe('*')
    }
  }, [loadBanners, reloadCurrentPosts])

  const filteredPosts = posts
  const filteredFeatured = featuredPosts
  const filteredFeaturedIds = new Set(filteredFeatured.map((post) => post.id))
  const filteredRegular = filteredPosts.filter((post) => !filteredFeaturedIds.has(post.id))
  const shownBanner = banners[activeBanner % Math.max(banners.length, 1)]
  const hasMorePosts = posts.length < totalPosts

  useEffect(() => {
    if (banners.length < 2) return
    const timer = window.setInterval(() => setActiveBanner((current) => (current + 1) % banners.length), 6000)
    return () => window.clearInterval(timer)
  }, [banners.length])

  const loadMorePosts = async () => {
    if (loadingMore || !hasMorePosts) return
    setLoadingMore(true)
    try {
      const nextPage = postPage + 1
      const response = await loadPostsPage(activeCategory, nextPage, postsPerPage)
      setPosts((current) => [...current, ...response.items])
      setTotalPosts(response.totalItems)
      setPostPage(response.page)
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <main className="bio-page">
      <header className="bio-header">
        <div className="bio-profile">
          <span className="bio-profile-avatar" aria-hidden="true">
            <img src="/logo-lorena.png" alt="" />
          </span>
          <span className="bio-profile-name">lorenamagazine</span>
        </div>
        <img className="bio-main-logo" src="/logo-lorena.png" alt="Lorena" />
        <p>Click na capa para ler a matéria completa</p>
      </header>

      <div className="bio-content">
        {shownBanner && (
          <section className="bio-banners" aria-label="Anúncios">
            <BioBanner banner={shownBanner} activeIndex={activeBanner} total={banners.length} />
            {banners.length > 1 && (
              <div className="bio-banner-controls">
                <button type="button" onClick={() => setActiveBanner((current) => (current - 1 + banners.length) % banners.length)} aria-label="Anúncio anterior"><ArrowLeft size={18} /></button>
                <div className="bio-banner-dots" aria-hidden="true">
                  {banners.map((banner, index) => <span key={banner.id} className={index === activeBanner ? 'active' : ''} />)}
                </div>
                <button type="button" onClick={() => setActiveBanner((current) => (current + 1) % banners.length)} aria-label="Próximo anúncio"><ArrowRight size={18} /></button>
              </div>
            )}
          </section>
        )}

        <section className="bio-feed-section" aria-label="Matérias">
          <div className="bio-feed-heading">
            <span>EDIÇÃO DIGITAL</span>
            <span>{String(totalPosts).padStart(2, '0')} MATÉRIAS</span>
          </div>
          <nav className="bio-category-filter" aria-label="Filtrar matérias por categoria">
            <button type="button" className={activeCategory === 'TODAS' ? 'active' : ''} onClick={() => setActiveCategory('TODAS')}>TODAS</button>
            {BIO_CATEGORIES.map((category) => <button key={category} type="button" className={activeCategory === category ? 'active' : ''} onClick={() => setActiveCategory(category)}>{category}</button>)}
          </nav>
          {loading ? (
            <p className="bio-empty">Carregando seleção…</p>
          ) : filteredPosts.length ? (
            <>
              {filteredFeatured.length > 0 && (
                <section className="bio-featured-section" aria-label="Matérias em destaque">
                  <div className="bio-featured-kicker"><span>EM DESTAQUE</span><span>SELEÇÃO LORENA</span></div>
                  <div className="bio-feed bio-featured-grid">
                    {filteredFeatured.map((post) => <BioPostCard key={post.id} post={post} />)}
                  </div>
                </section>
              )}
              {filteredRegular.length > 0 && (
                <div className="bio-feed">
                  {filteredRegular.map((post) => <BioPostCard key={post.id} post={post} />)}
                </div>
              )}
              {hasMorePosts && (
                <div className="bio-load-more">
                  <span>{posts.length} de {totalPosts} matérias exibidas</span>
                  <button type="button" disabled={loadingMore} onClick={loadMorePosts}>
                    {loadingMore ? 'Carregando...' : 'Ver mais'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="bio-empty">{activeCategory === 'TODAS' ? 'Novas matérias em breve.' : 'Nenhuma matéria nesta categoria.'}</p>
          )}
        </section>
      </div>

      <footer className="bio-footer">
        <img src="/logo-lorena.png" alt="Lorena" />
        <span>JORNALISMO, CULTURA E COMPORTAMENTO</span>
      </footer>
    </main>
  )
}

function loadPostsPage(category: string, page: number, perPage: number) {
  const filter = postFilter(category)
  return pb.collection('link_bio_items').getList<BioItem>(page, perPage, { filter, sort: 'position' })
}

function loadFeaturedPosts(category: string) {
  return pb.collection('link_bio_items').getFullList<BioItem>({ filter: `${postFilter(category)} && featured = true`, sort: 'position' }).then((items) => items.slice(0, 3))
}

function postFilter(category: string) {
  return category === 'TODAS' ? 'kind = "post"' : `kind = "post" && tags ~ "${category}"`
}

function BioBanner({ banner, activeIndex, total }: { banner: BioItem; activeIndex: number; total: number }) {
  const href = banner.link?.trim() ? normalizeBioLink(banner.link) : ''
  const label = banner.title ? `Abrir anúncio: ${banner.title}` : 'Abrir anúncio'

  return <div key={banner.id} className={`bio-banner${href ? ' has-link' : ''}`}>
    {banner.html ? <iframe className="bio-banner-html" srcDoc={banner.html} title={banner.title || 'Banner HTML5'} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation" /> : <img src={bioItemImageUrl(banner)} alt={banner.title || ''} />}
    {href && <a className="bio-banner-click-target" href={href} aria-label={label} />}
    <span className="bio-banner-index">{String(activeIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
  </div>
}

function BioPostCard({ post }: { post: BioItem }) {
  return <a className="bio-feed-card" href={normalizeBioLink(post.link)} aria-label={post.title || 'Abrir matéria'}>
    <img src={bioItemImageUrl(post)} alt={post.title || ''} loading="lazy" />
    <span className="bio-feed-hover"><strong className="bio-feed-title">{post.title || 'Abrir matéria'}</strong><ArrowUpRight size={24} /></span>
  </a>
}
